import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, Mic, MicOff, Video, VideoOff, PhoneOff, Copy, Check, 
  UserMinus, VolumeX, Crown, Share2, ArrowLeft
} from "lucide-react";
import { Button } from "./ui/button";


import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const WatchPartyPage = () => {
  const { partyCode } = useParams();
  const navigate = useNavigate();
  const [party, setParty] = useState(null);
  const [broadcast, setBroadcast] = useState(null);
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  
  // Local media state
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  
  const localVideoRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) {
      navigate(`/login-user?return=/watch-party/${partyCode}`);
      return;
    }

    const joinParty = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Join the party
        const res = await axios.post(`${API}/watch-party/join/${partyCode}`, {}, { headers });
        setParty(res.data.party);
        setBroadcast(res.data.party.broadcast);
        setChannel(res.data.party.channel);
        
        // Start polling for updates
        pollInterval.current = setInterval(async () => {
          try {
            const updateRes = await axios.get(`${API}/watch-party/${res.data.party.id}`, { headers });
            setParty(updateRes.data.party);
            if (updateRes.data.party.channel) {
              setChannel(updateRes.data.party.channel);
            }
          } catch (e) {
            if (e.response?.status === 404 || e.response?.status === 403) {
              // Party ended or kicked
              clearInterval(pollInterval.current);
              navigate("/");
            }
          }
        }, 3000);
        
        // Initialize local media
        initializeMedia();
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to join watch party");
      } finally {
        setLoading(false);
      }
    };

    joinParty();

    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [partyCode, navigate]);

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing media devices:", err);
      // Continue without local media
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => {
        track.enabled = !localAudioEnabled;
      });
      setLocalAudioEnabled(!localAudioEnabled);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => {
        track.enabled = !localVideoEnabled;
      });
      setLocalVideoEnabled(!localVideoEnabled);
    }
  };

  const leaveParty = async () => {
    const token = localStorage.getItem("userToken");
    try {
      await axios.post(`${API}/watch-party/${party.id}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Error leaving party:", err);
    }
    
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (pollInterval.current) clearInterval(pollInterval.current);
    navigate("/");
  };

  const kickParticipant = async (participantId) => {
    const token = localStorage.getItem("userToken");
    try {
      await axios.post(`${API}/watch-party/${party.id}/kick`, 
        { participant_id: participantId, action: "kick" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("Error kicking participant:", err);
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/watch-party/${party.code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500">Joining watch party...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 pb-16 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-zinc-900/50 border border-white/10 p-8">
              <Users className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="font-secondary text-xl font-bold uppercase text-white mb-4">
                Unable to Join
              </h1>
              <p className="text-zinc-500 mb-6">{error}</p>
              <Link to="/">
                <Button className="bg-white text-black hover:bg-zinc-200 font-secondary text-xs px-4 py-2 h-auto rounded-none">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const currentUserId = JSON.parse(localStorage.getItem("userData") || "{}")?.id;
  const isHost = party?.host_id === currentUserId;
  const participants = party?.participants || [];

  return (
    <main className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header Bar */}
      <div className="bg-zinc-900 border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={leaveParty} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            {/* Channel Logo */}
            {channel && (
              <div className="flex items-center gap-2 pr-3 border-r border-white/10">
                {channel.logo_url ? (
                  <img src={channel.logo_url} alt={channel.name} className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-orange-500 rounded flex items-center justify-center text-white text-xs font-bold">
                    {channel.abbreviation || channel.name?.charAt(0) || "CH"}
                  </div>
                )}
                <span className="text-xs text-zinc-400 font-medium">{channel.abbreviation || channel.name}</span>
              </div>
            )}
            <div>
              <h1 className="font-secondary text-sm font-bold uppercase tracking-widest text-white">
                {party?.name}
              </h1>
              <p className="text-xs text-zinc-500">
                {participants.length}/{party?.max_participants || 5} participants
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={copyInviteLink}
            className="bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white font-secondary text-xs px-3 py-1.5 h-auto rounded-none"
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Share2 className="w-4 h-4 mr-1" />}
            {copied ? "Copied!" : "Invite"}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Video Player Area */}
        <div className="flex-1 bg-black flex items-center justify-center p-4">
          {broadcast ? (
            <div className="w-full max-w-5xl aspect-video bg-zinc-900 flex items-center justify-center">
              {/* In a real implementation, this would be the HLS video player */}
              <div className="text-center">
                <p className="text-white font-secondary text-lg mb-2">{broadcast.title}</p>
                <p className="text-zinc-500 text-sm">{broadcast.sport}</p>
                {broadcast.is_live && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-red-600 text-white text-xs">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    LIVE
                  </span>
                )}
                <p className="text-zinc-600 text-xs mt-4">
                  Video player would be displayed here
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center text-zinc-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No broadcast selected</p>
              <p className="text-xs mt-2">Enjoy watching with your party!</p>
            </div>
          )}
        </div>

        {/* Participants Strip */}
        <div className="bg-zinc-900 border-t border-white/10">
          <div className="p-4">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              {/* Local User */}
              <div className="flex-shrink-0 relative">
                <div className="w-32 h-24 bg-zinc-800 rounded overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className={`w-full h-full object-cover ${!localVideoEnabled ? 'hidden' : ''}`}
                  />
                  {!localVideoEnabled && (
                    <div className="w-full h-full flex items-center justify-center">
                      <VideoOff className="w-8 h-8 text-zinc-600" />
                    </div>
                  )}
                </div>
                <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
                  <span className="text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded truncate max-w-[80px]">
                    You {isHost && <Crown className="w-3 h-3 inline ml-1 text-yellow-500" />}
                  </span>
                  {!localAudioEnabled && <MicOff className="w-3 h-3 text-red-500" />}
                </div>
              </div>

              {/* Other Participants */}
              {participants
                .filter(p => p.id !== currentUserId)
                .map((participant) => (
                  <div key={participant.id} className="flex-shrink-0 relative group">
                    <div className="w-32 h-24 bg-zinc-800 rounded overflow-hidden flex items-center justify-center">
                      <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center overflow-hidden">
                        {participant.profile_picture ? (
                          <img src={participant.profile_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-bold text-lg">
                            {participant.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
                      <span className="text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded truncate max-w-[80px]">
                        {participant.name}
                        {participant.is_host && <Crown className="w-3 h-3 inline ml-1 text-yellow-500" />}
                      </span>
                      {participant.is_muted && <VolumeX className="w-3 h-3 text-red-500" />}
                    </div>
                    
                    {/* Host Controls */}
                    {isHost && !participant.is_host && (
                      <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => kickParticipant(participant.id)}
                          className="p-1 bg-red-500/80 hover:bg-red-500 rounded"
                          title="Remove from party"
                        >
                          <UserMinus className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              
              {/* Empty Slots */}
              {Array.from({ length: (party?.max_participants || 5) - participants.length }).map((_, idx) => (
                <div key={`empty-${idx}`} className="flex-shrink-0">
                  <div className="w-32 h-24 bg-zinc-800/50 rounded border-2 border-dashed border-zinc-700 flex items-center justify-center">
                    <Users className="w-6 h-6 text-zinc-700" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="border-t border-white/10 p-4 flex items-center justify-center gap-4">
            <button
              onClick={toggleAudio}
              className={`p-3 rounded-full ${localAudioEnabled ? 'bg-zinc-800 text-white' : 'bg-red-500 text-white'}`}
            >
              {localAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
            <button
              onClick={toggleVideo}
              className={`p-3 rounded-full ${localVideoEnabled ? 'bg-zinc-800 text-white' : 'bg-red-500 text-white'}`}
            >
              {localVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </button>
            <button
              onClick={leaveParty}
              className="p-3 rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              <PhoneOff className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default WatchPartyPage;
